import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ArrowRight, Check, Circle, X } from "lucide-react";
import { cn } from "@/lib/utils";

type ChecklistItem = {
  id: string;
  label: string;
  description: string;
  to: string;
  done: boolean;
};

// First-run activation checklist. Computed entirely from data the dashboard
// already fetches (session, partner, deal summary), so it adds no new queries.
// Hidden once every item is done or the partner dismisses it (persisted in
// localStorage per partner so it doesn't nag returning users).
export function FirstRunChecklist({
  partnerId,
  profileComplete,
  integrationsSet,
  firstDealSubmitted,
}: {
  partnerId: string;
  profileComplete: boolean;
  integrationsSet: boolean;
  firstDealSubmitted: boolean;
}) {
  const storageKey = `colab:first-run-dismissed:${partnerId}`;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(storageKey) === "1");
    } catch {
      setDismissed(false);
    }
  }, [storageKey]);

  const items: ChecklistItem[] = [
    {
      id: "profile",
      label: "Complete your profile",
      description: "Add your name and role so we know who we're working with.",
      to: "/portal/profile",
      done: profileComplete,
    },
    {
      id: "integrations",
      label: "Add the systems you integrate with",
      description: "Tell us which PMSs or platforms you connect to.",
      to: "/portal/profile",
      done: integrationsSet,
    },
    {
      id: "first-deal",
      label: "Register your first deal",
      description: "Submit a lead to start building attributed pipeline.",
      to: "/portal/deals/new",
      done: firstDealSubmitted,
    },
  ];

  const doneCount = items.filter((i) => i.done).length;
  const allDone = doneCount === items.length;

  if (dismissed || allDone) return null;

  function dismiss() {
    try {
      localStorage.setItem(storageKey, "1");
    } catch {
      // ignore
    }
    setDismissed(true);
  }

  return (
    <section className="mb-7 rounded-2xl border bg-card p-6 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Get set up
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            A few quick steps to get the most out of your partner portal.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-muted-foreground tabular-nums">
            {doneCount}/{items.length}
          </span>
          <button
            type="button"
            onClick={dismiss}
            className="text-muted-foreground transition hover:text-foreground"
            aria-label="Dismiss setup checklist"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <ul className="mt-4 space-y-2">
        {items.map((item) => (
          <li key={item.id}>
            <Link
              to={item.to}
              className={cn(
                "group flex items-center gap-3 rounded-lg border p-3 transition",
                item.done
                  ? "border-border bg-muted/20"
                  : "border-border hover:border-accent",
              )}
            >
              {item.done ? (
                <Check className="h-5 w-5 shrink-0 text-emerald-600" />
              ) : (
                <Circle className="h-5 w-5 shrink-0 text-muted-foreground/50" />
              )}
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    "text-sm font-medium",
                    item.done
                      ? "text-muted-foreground line-through"
                      : "text-foreground",
                  )}
                >
                  {item.label}
                </span>
                {!item.done ? (
                  <span className="block text-xs text-muted-foreground">
                    {item.description}
                  </span>
                ) : null}
              </span>
              {!item.done ? (
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-accent" />
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
